import React from 'react';

export type FaqItem = { q: string; a: string };

type FAQProps = {
  items: FaqItem[];
  palette?: 'indigo' | 'violet' | 'emerald' | 'rose' | 'amber' | 'sky' | 'slate';
  style?: 'classic' | 'card' | 'underline';
  showIcons?: boolean;
  icon?: string;
  editable?: boolean;
  onChange?: (items: FaqItem[]) => void;
};

export default function FAQ({ items, palette = 'indigo', style = 'classic', showIcons = false, icon }: FAQProps) {
  const acc = {
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
    violet: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
    sky: { bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
  } as const;
  const p = acc[palette] || acc.indigo;

  const itemWrapper = style === 'card'
    ? 'rounded-xl border border-gray-200 p-3 hover:border-gray-300 transition'
    : style === 'underline'
      ? 'py-3 border-b border-gray-200 last:border-b-0'
      : 'py-3';

  return (
    <div className={style === 'classic' ? 'divide-y divide-gray-200' : ''}>
      {(items || []).map((it, i) => (
        <div key={i} className={itemWrapper}>
          <details className="group">
            <summary className="list-none flex items-start justify-between cursor-pointer">
              <span className="text-gray-900 font-medium inline-flex items-center gap-2">
                {showIcons && (
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${p.bg} ${p.text} border ${p.border}`}>
                    {icon === 'star' && (<svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>)}
                    {(!icon || icon==='help') && (<svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>)}
                    {icon === 'check' && (<svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>)}
                    {icon === 'info' && (<svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>)}
                    {icon === 'bolt' && (<svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>)}
                    {icon === 'sparkles' && (<svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.5 3.5L17 8l-3.5 1.5L12 13l-1.5-3.5L7 8l3.5-1.5L12 3z"/><path d="M5 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z"/><path d="M18 14l.75 1.75L20.5 16.5 18.75 17.25 18 19l-.75-1.75L15.5 16.5l1.75-.75L18 14z"/></svg>)}
                  </span>
                )}
                {it.q || 'Question'}
              </span>
              <span className={`ml-3 mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full ${p.bg} ${p.text} border ${p.border} group-open:rotate-45 transition-transform`}>
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </span>
            </summary>
            <div className="mt-2 text-gray-700 leading-relaxed">{it.a || 'Answer'}</div>
          </details>
        </div>
      ))}
    </div>
  );
}
